import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { FC } from "react";

import { m } from "@recobra/i18n/messages";
import { Badge } from "@recobra/ui/badge";

import { caseSummaryQueryOptions } from "../../cases";
import { clientsApi } from "../../lib/api";

export const TaskCreationContextHeading: FC<{
  isOpen: boolean;
  contextType: "case" | "client";
  contextId: string;
}> = ({ isOpen, contextType, contextId }) =>
  contextType === "case" ? (
    <CaseContextHeading isOpen={isOpen} contextId={contextId} />
  ) : (
    <ClientContextHeading isOpen={isOpen} contextId={contextId} />
  );

const CaseContextHeading: FC<{ isOpen: boolean; contextId: string }> = ({ isOpen, contextId }) => {
  const queryClient = useQueryClient();
  const { data: kase } = useQuery({
    ...caseSummaryQueryOptions(queryClient, contextId),
    enabled: isOpen && !!contextId,
  });

  return (
    <div className="flex min-w-0 items-center gap-2">
      <span className="truncate text-sm text-muted-foreground">
        {m.task_context_type_label({ contextType: "case" })}{" "}
        <span className="font-mono">{kase ? `#${kase.caseNumber}` : m.common_em_dash()}</span>
      </span>
      {kase ? (
        <Badge variant="secondary" title={m.case_type_label({ caseType: kase.caseType })}>
          {m.case_type_label({ caseType: kase.caseType })}
        </Badge>
      ) : null}
    </div>
  );
};

const ClientContextHeading: FC<{ isOpen: boolean; contextId: string }> = ({
  isOpen,
  contextId,
}) => {
  const { data: client } = useQuery({
    queryKey: ["clients", "byId", contextId],
    queryFn: () => clientsApi.get(contextId),
    enabled: isOpen && !!contextId,
  });

  return (
    <span className="truncate text-sm text-muted-foreground">
      {m.task_context_type_label({ contextType: "client" })} {client?.name ?? m.common_em_dash()}
    </span>
  );
};
